import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Save } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import CustomerMemoRichEditor, { memoContentToHtml } from '../components/CustomerMemoRichEditor'
import { api, getApiErrorMessage } from '../lib/api'
import { customerMemoDraftKey } from '../lib/customerMemoDraftStorage'
import { useToast } from '../ui/ToastProvider'

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function memoTitleFromFirstLine(value) {
  const firstLine = String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean)
  return firstLine ? firstLine.replace(/^#+\s*/, '').slice(0, 255) : ''
}

function memoHashTagsFromText(value) {
  const tags = []
  const re = /(^|\s)#([^#\s,.;:!?()[\]{}]{1,50})/g
  let match
  while ((match = re.exec(String(value || ''))) !== null) {
    const tag = String(match[2] || '').trim()
    if (tag && !tags.some((item) => item.toLowerCase() === tag.toLowerCase())) tags.push(tag)
    if (tags.length >= 12) break
  }
  return tags
}

function memoAttachmentPayload(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter((att) => att?.fileId)
    .map((att, idx) => ({ fileId: att.fileId, caption: att.caption || null, sortOrder: idx }))
}

function sortCustomerMemoRows(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const pinnedCompare = (b?.pinned === true ? 1 : 0) - (a?.pinned === true ? 1 : 0)
    if (pinnedCompare) return pinnedCompare
    return String(b?.updatedAt || b?.createdAt || '').localeCompare(String(a?.updatedAt || a?.createdAt || ''))
  })
}

export default function CustomerMemoAppEditorPage() {
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user: authUser } = useAuth()
  const customerId = Number(params.id)
  const requestedMemoId = String(searchParams.get('memoId') || '').trim()
  const requestedNewMemo = searchParams.get('new') === '1'
  const { show } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [memoId, setMemoId] = useState(null)
  const [memoVersion, setMemoVersion] = useState(null)
  const [memoText, setMemoText] = useState('')
  const [memoContent, setMemoContent] = useState(null)
  const [memoAttachments, setMemoAttachments] = useState([])
  const [memoPinned, setMemoPinned] = useState(false)
  const [draftStatus, setDraftStatus] = useState('')
  const [loadedOnce, setLoadedOnce] = useState(false)

  const draftStorageKey = useMemo(
    () => customerMemoDraftKey({
      user: authUser,
      surface: 'app-editor',
      customerId,
      memoId: requestedNewMemo ? 'new' : (requestedMemoId || 'latest'),
    }),
    [authUser, customerId, requestedMemoId, requestedNewMemo],
  )

  const applyMemo = useCallback((memo) => {
    if (!memo) {
      setMemoId(null)
      setMemoVersion(null)
      setMemoText('')
      setMemoContent(null)
      setMemoAttachments([])
      setMemoPinned(false)
      setDraftStatus('')
      return
    }
    setMemoId(memo.id || null)
    setMemoVersion(memo.version ?? null)
    setMemoText(memo.plainText || '')
    setMemoContent(memo.content || null)
    setMemoAttachments(Array.isArray(memo.attachments) ? memo.attachments : [])
    setMemoPinned(memo.pinned === true)
    setDraftStatus('저장됨')
  }, [])

  const loadMemo = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0) {
      setError('고객 정보를 확인할 수 없습니다.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/memos`, { __silent: true })
      const rows = sortCustomerMemoRows(unwrap(res))
      const current = requestedNewMemo
        ? null
        : requestedMemoId
        ? rows.find((row) => String(row?.id ?? '') === requestedMemoId) || null
        : rows[0] || null
      if (requestedMemoId && !current) {
        applyMemo(null)
        setError('선택한 메모를 찾을 수 없습니다.')
        setLoadedOnce(true)
        return
      }
      applyMemo(current)
      try {
        const raw = window.sessionStorage?.getItem(draftStorageKey)
        if (raw) {
          const draft = JSON.parse(raw)
          const sameMemo = String(draft.memoId ?? '') === String(current?.id ?? '')
          if (sameMemo) {
            setMemoText(draft.plainText || '')
            setMemoContent(draft.content || null)
            setMemoAttachments(Array.isArray(draft.attachments) ? draft.attachments : [])
            setMemoPinned(draft.pinned === true)
            setMemoVersion(draft.version ?? current?.version ?? null)
            setDraftStatus(
              draft.version != null && current?.version != null && Number(draft.version) !== Number(current.version)
                ? '임시저장 복원됨 · 서버 변경 확인 필요'
                : '임시저장 복원됨',
            )
          }
        }
      } catch {}
      setLoadedOnce(true)
    } catch (e) {
      const msg = getApiErrorMessage(e, '고객 메모를 불러오지 못했습니다.')
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [applyMemo, customerId, draftStorageKey, requestedMemoId, requestedNewMemo])

  useEffect(() => {
    loadMemo()
  }, [loadMemo])

  useEffect(() => {
    if (!loadedOnce || loading || saving || !draftStorageKey || draftStatus !== '작성 중') return
    const hasDraft = Boolean(memoText.trim() || memoAttachments.length > 0)
    try {
      if (!hasDraft) {
        window.sessionStorage?.removeItem(draftStorageKey)
        return
      }
      window.sessionStorage?.setItem(draftStorageKey, JSON.stringify({
        plainText: memoText,
        content: memoContent,
        attachments: memoAttachments,
        pinned: memoPinned,
        memoId,
        version: memoVersion,
      }))
    } catch {}
  }, [draftStatus, draftStorageKey, loadedOnce, loading, memoAttachments, memoContent, memoId, memoPinned, memoText, memoVersion, saving])

  const saveMemo = useCallback(async () => {
    if (!Number.isFinite(customerId) || customerId <= 0 || saving) return
    if (!memoText.trim() && memoAttachments.length === 0) {
      show('메모 내용을 입력해 주세요.', 'error')
      return
    }
    setSaving(true)
    setDraftStatus('저장 중')
    setError('')
    try {
      const body = {
        title: memoTitleFromFirstLine(memoText) || null,
        plainText: memoText,
        pinned: memoPinned,
        version: memoId ? memoVersion : null,
        category: null,
        tags: memoHashTagsFromText(memoText),
        content: memoContent || {
          schemaVersion: 1,
          blocks: memoText.trim() ? [{ type: 'paragraph', text: memoText }] : [],
        },
        attachments: memoAttachmentPayload(memoAttachments),
      }
      const res = memoId
        ? await api.patch(`/customer-management/customers/${customerId}/memos/${memoId}`, body, { __silent: true })
        : await api.post(`/customer-management/customers/${customerId}/memos`, body, { __silent: true })
      const saved = unwrap(res) || {}
      setMemoId(saved.id || memoId)
      setMemoVersion(saved.version ?? null)
      setMemoText(saved.plainText || memoText)
      setMemoContent(saved.content || memoContent)
      setMemoAttachments(Array.isArray(saved.attachments) ? saved.attachments : memoAttachments)
      try { window.sessionStorage?.removeItem(draftStorageKey) } catch {}
      setDraftStatus('저장됨')
      show('고객 메모가 저장되었습니다.', 'success')
      if (saved.id) setSearchParams({ memoId: String(saved.id) }, { replace: true })
    } catch (e) {
      const msg = getApiErrorMessage(e, '고객 메모 저장에 실패했습니다.')
      setError(msg)
      setDraftStatus('저장 실패')
      show(msg, 'error', 5000)
    } finally {
      setSaving(false)
    }
  }, [customerId, draftStorageKey, memoAttachments, memoContent, memoId, memoPinned, memoText, memoVersion, saving, setSearchParams, show])

  return (
    <div className="customer-memo-app-editor min-h-screen bg-slate-50 text-slate-900" data-testid="customer-memo-app-editor-page">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold">고객메모 편집</h1>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {draftStatus || (loading ? '불러오는 중' : '작성 가능')}
            </div>
          </div>
          <div className="flex shrink-0 items-center">
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded bg-brand-navy px-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              disabled={loading || saving}
              data-testid="customer-memo-app-editor-save"
              onClick={saveMemo}
            >
              <Save size={16} aria-hidden="true" />
              저장
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto flex min-h-[calc(100vh-4.5rem)] max-w-4xl flex-col gap-3 p-3 sm:p-4">
        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {loading ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500">
            고객 메모를 불러오는 중입니다.
          </div>
        ) : (
          <CustomerMemoRichEditor
            uploadPath="/files/upload/customer-memo"
            valueHtml={memoContentToHtml(memoContent, memoText)}
            plainText={memoText}
            attachments={memoAttachments}
            disabled={saving}
            placeholder="첫 줄은 제목처럼 사용됩니다. #태그, 이미지, 링크를 함께 남겨보세요."
            toolbarMode="compact"
            className="min-h-0 flex flex-1 flex-col"
            shellClassName="customer-memo-tiptap-scroll min-h-0 flex flex-1 flex-col"
            contentClassName="customer-memo-editor-content min-h-0 flex-1 overflow-y-auto"
            onChange={(next) => {
              setMemoText(next.plainText)
              setMemoContent(next.content)
              if (!saving) setDraftStatus('작성 중')
            }}
            onAttachmentsChange={(next) => {
              setMemoAttachments(next)
              if (!saving) setDraftStatus('작성 중')
            }}
          />
        )}
      </main>
    </div>
  )
}